import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, TextInput,
  TouchableOpacity, Platform, Modal,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useToast } from '../../../../components/LhToastContainer';
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';

interface BulkAddRoutesModalProps {
  visible: boolean;
  onClose: () => void;
  locationId: string | number;
  locationName: string;
  onSuccess: () => void;
  routeTypeOptions?: { label: string; value: string }[];
}

const BulkAddRoutesModal: React.FC<BulkAddRoutesModalProps> = ({
  visible, onClose, locationId, locationName, onSuccess, routeTypeOptions = [],
}) => {
  const toast = useToast();
  const [form, setForm] = useState({ routeNumbers: '', routeType: '' });
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [errors, setErrors] = useState<{ routeNumbers?: string; routeType?: string }>({});

  // Unsaved changes only matter before the API result is shown
  const hasUnsavedChanges =
    resultMessage === null && (form.routeNumbers.trim() !== '' || form.routeType !== '');

  const handleClose = useCallback(() => {
    setForm({ routeNumbers: '', routeType: '' });
    setResultMessage(null);
    setShowDiscardConfirm(false);
    setErrors({});
    onClose();
  }, [onClose]);

  const attemptClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      handleClose();
    }
  }, [hasUnsavedChanges, handleClose]);

  const handleSubmit = useCallback(async () => {
    const newErrors: typeof errors = {};
    const lines = form.routeNumbers
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const l of lines) {
      const key = l.toLowerCase();
      if (!seen.has(key)) { seen.add(key); deduped.push(l); }
    }
    const skipped = lines.length - deduped.length;
    
    if (deduped.length === 0) {
      newErrors.routeNumbers = 'At least one route number is required';
    }
    if (!form.routeType) {
      newErrors.routeType = 'Route type is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    try {
      setIsSubmitting(true);
      const api = createApiClient();
      const res = await api.post<{ message: string; data: any }>(
        `/perfect-truck/locations/${locationId}/routes/bulk-add`,
        { routeNumbers: deduped, routeType: form.routeType },
        {
          params: {
            locationName,
            clientTimestamp: getLocalTimestamp(),
            clientTimezone: getTimezoneOffsetString(),
          },
        },
      );
      const msg = res.data?.message ?? `${deduped.length} route(s) processed.`;
      setResultMessage(msg);
      onSuccess();
    } catch (err: any) {
      toast.show(
        err?.response?.data?.error ??
        err?.response?.data?.message ??
        'Failed to add routes',
        'error',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [form, locationId, locationName, onClose, onSuccess]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={attemptClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.content, { maxWidth: 560 }]}>
          <View style={styles.header}>
            <Text style={styles.title}>Bulk Add Routes</Text>
            <TouchableOpacity onPress={attemptClose} activeOpacity={0.7}>
              <Text style={styles.closeButton}>×</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.body}>
            {/* Result message banner – shown after a successful submit */}
            {resultMessage !== null && (
              <View style={styles.resultBanner}>
                <MaterialIcons name="check-circle" size={18} color={AdminColors.green600} style={{ marginTop: 1, flexShrink: 0 }} />
                <Text style={styles.resultBannerText}>{resultMessage}</Text>
              </View>
            )}

            {/* Info banner – hidden once result is shown */}
            {resultMessage === null && (
              <View style={styles.infoBanner}>
                <MaterialIcons name="check" size={16} color={AdminColors.blue700} style={{ marginTop: 1, flexShrink: 0 }} />
                <Text style={styles.infoBannerText}>
                  Paste multiple route numbers below, one per line. Duplicates (case-insensitive) will be skipped automatically.
                </Text>
              </View>
            )}

            {/* Location (read-only) */}
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>
                Location <Text style={styles.required}>*</Text>
              </Text>
              <View style={styles.disabledField}>
                <Text style={styles.fieldText}>{locationName}</Text>
              </View>
            </View>

            {/* Route Numbers textarea – hidden once result is shown */}
            {resultMessage === null && (
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>
                  Route Numbers <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={errors.routeNumbers ? { ...styles.textarea, ...styles.inputError } : styles.textarea}
                  multiline
                  numberOfLines={6}
                  placeholder={'R-201\nR-202\nR-203\nR-204'}
                  placeholderTextColor={AdminColors.textMuted}
                  value={form.routeNumbers}
                  onChangeText={(text) => {
                    setForm({ ...form, routeNumbers: text });
                    if (errors.routeNumbers) setErrors({ ...errors, routeNumbers: undefined });
                  }}
                  textAlignVertical="top"
                />
                {errors.routeNumbers && (
                  <Text style={styles.errorText}>{errors.routeNumbers}</Text>
                )}
                <Text style={styles.hintText}>Enter one route number per line</Text>
              </View>
            )}

            {/* Route Type – hidden once result is shown */}
            {resultMessage === null && (
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>
                  Route Type <Text style={styles.required}>*</Text>
                </Text>
                {Platform.OS === 'web' ? (
                  <>
                    <select
                      style={errors.routeType ? { ...styles.selectWeb, ...styles.selectError } : styles.selectWeb as any}
                      value={form.routeType}
                      onChange={(e: any) => {
                        setForm({ ...form, routeType: e.target.value });
                        if (errors.routeType) setErrors({ ...errors, routeType: undefined });
                      }}
                    >
                      <option value="">Select route type...</option>
                      {routeTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.routeType && (
                      <Text style={styles.errorText}>{errors.routeType}</Text>
                    )}
                  </>
                ) : (
                  <>
                    <View style={errors.routeType ? { ...styles.selectNative, ...styles.inputError } : styles.selectNative}>
                      <Text style={styles.fieldText}>
                        {form.routeType
                          ? routeTypeOptions.find(o => o.value === form.routeType)?.label
                          : 'Select route type...'}
                      </Text>
                    </View>
                    {errors.routeType && (
                      <Text style={styles.errorText}>{errors.routeType}</Text>
                    )}
                  </>
                )}
              </View>
            )}
          </View>

          <View style={styles.buttonRow}>
            {resultMessage !== null ? (
              // After result: only show Close button
              <TouchableOpacity
                style={[styles.button, styles.buttonSave]}
                onPress={handleClose}
                activeOpacity={0.7}
              >
                <Text style={styles.buttonSaveText}>Close</Text>
              </TouchableOpacity>
            ) : (
              // Before result: Cancel + Add Routes
              <>
                <TouchableOpacity
                  style={[styles.button, styles.buttonCancel]}
                  onPress={attemptClose}
                  activeOpacity={0.7}
                >
                  <Text style={styles.buttonCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.buttonSave, isSubmitting && styles.buttonDisabled]}
                  onPress={handleSubmit}
                  activeOpacity={0.7}
                  disabled={isSubmitting}
                >
                  <Text style={styles.buttonSaveText}>
                    {isSubmitting ? 'Adding…' : 'Add Routes'}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* Discard confirmation overlay */}
          {showDiscardConfirm && (
            <View style={styles.discardOverlay}>
              <View style={styles.discardCard}>
                <Text style={styles.discardTitle}>Discard changes?</Text>
                <Text style={styles.discardMessage}>
                  You have unsaved changes. Are you sure you want to close without saving?
                </Text>
                <View style={styles.discardButtons}>
                  <TouchableOpacity
                    style={[styles.button, styles.buttonCancel, { flex: 1 }]}
                    onPress={() => setShowDiscardConfirm(false)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.buttonCancelText}>Keep Editing</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.button, styles.discardButton, { flex: 1 }]}
                    onPress={handleClose}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.discardButtonText}>Discard</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 12,
    width: '90%',
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  title: {
    fontSize: 20,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    fontWeight: '600',
  },
  closeButton: {
    fontSize: 32,
    color: AdminColors.textMuted,
    marginTop: -6,
    lineHeight: 24,
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 20,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  infoBannerText: {
    flex: 1,
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textSecondary,
    lineHeight: 20,
  },
  fieldGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: AdminColors.textPrimary,
    fontWeight: '500',
  },
  required: {
    color: '#DC2626',
  },
  disabledField: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgPage,
    justifyContent: 'center',
    minHeight: 40,
  },
  textarea: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    minHeight: 120,
    outlineWidth: 0,
  } as any,
  inputError: {
    borderColor: '#DC2626',
  },
  errorText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#DC2626',
    marginTop: 4,
  },
  hintText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: AdminColors.textMuted,
    marginTop: 4,
  },
  selectWeb: {
    width: '100%',
    height: 40,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderStyle: 'solid' as any,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    cursor: 'pointer',
    appearance: 'auto' as any,
    boxSizing: 'border-box' as any,
  } as any,
  selectError: {
    borderColor: '#DC2626',
  } as any,
  selectNative: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    justifyContent: 'center',
    minHeight: 40,
  },
  fieldText: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 24,
    paddingBottom: 24,
    justifyContent: 'flex-end',
  },
  button: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 28,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonCancel: {
    borderWidth: 2,
    borderColor: AdminColors.blue700,
    backgroundColor: AdminColors.bgSurface,
  },
  buttonCancelText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: AdminColors.blue700,
    fontWeight: '700',
  },
  buttonSave: {
    backgroundColor: AdminColors.blue700,
  },
  buttonSaveText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  resultBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 14,
    backgroundColor: AdminColors.green75,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.green600,
  },
  resultBannerText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: AdminColors.green600,
    lineHeight: 20,
  },
  discardOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  discardCard: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 24,
    width: '100%',
    gap: 12,
  },
  discardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    fontWeight: '700',
  },
  discardMessage: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textSecondary,
    lineHeight: 20,
  },
  discardButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  discardButton: {
    backgroundColor: AdminColors.red400,
    borderWidth: 0,
  },
  discardButtonText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

export default BulkAddRoutesModal;
